import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Megaphone, Plus, Trash2, ExternalLink, Edit } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

function GestionAnuncios() {
  const [anuncios, setAnuncios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    imagen_url: '',
    enlace: '',
    activo: true
  });

  useEffect(() => {
    if (user?.role !== 'entrenador') {
      window.location.href = '/';
      return;
    }
    fetchAnuncios();
  }, [user]);

  const fetchAnuncios = async () => {
    try {
      const res = await axios.get(`${API}/anuncios/todos`);
      setAnuncios(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.imagen_url || !formData.enlace) {
      alert('Rellena todos los campos');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/anuncios/${editingId}`, formData);
      } else {
        await axios.post(`${API}/anuncios`, formData);
      }
      setShowDialog(false);
      setEditingId(null);
      setFormData({ imagen_url: '', enlace: '', activo: true });
      fetchAnuncios();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (anuncio) => {
    setFormData({
      imagen_url: anuncio.imagen_url,
      enlace: anuncio.enlace,
      activo: anuncio.activo
    });
    setEditingId(anuncio.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este anuncio?')) return;
    try {
      await axios.delete(`${API}/anuncios/${id}`);
      fetchAnuncios();
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleToggleActivo = async (anuncio) => {
    try {
      await axios.put(`${API}/anuncios/${anuncio.id}`, {
        ...anuncio,
        activo: !anuncio.activo
      });
      fetchAnuncios();
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gold flex items-center gap-2">
              <Megaphone className="h-8 w-8" /> Gestión de Anuncios
            </h1>
            <p className="text-gray-400">Administra la publicidad de la app</p>
          </div>
          <Button className="btn-primary-mk" onClick={() => { setEditingId(null); setFormData({ imagen_url: '', enlace: '', activo: true }); setShowDialog(true); }}>
            <Plus className="h-5 w-5 mr-2" /> Nuevo Anuncio
          </Button>
        </div>

        {/* Info */}
        <Card className="bg-blue-900/30 border-blue-700 mb-6">
          <CardContent className="p-4">
            <p className="text-blue-300 text-sm">
              📍 Los anuncios se mostrarán en: <span className="text-white font-semibold">Noticias, Miscelánea, 100 Tiros y Words</span>
            </p>
          </CardContent>
        </Card>

        {/* Lista de anuncios */}
        {anuncios.length === 0 ? (
          <Card className="bg-gray-900 border-gray-700">
            <CardContent className="p-8 text-center">
              <Megaphone className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No hay anuncios creados</p>
              <Button className="mt-4 btn-primary-mk" onClick={() => setShowDialog(true)}>
                <Plus className="h-4 w-4 mr-2" /> Crear primer anuncio
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {anuncios.map(anuncio => (
              <Card key={anuncio.id} className={`bg-gray-900 border-gray-700 ${!anuncio.activo && 'opacity-50'}`}>
                <CardContent className="p-4">
                  <div className="relative mb-3">
                    <img 
                      src={anuncio.imagen_url} 
                      alt="Anuncio"
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs ${anuncio.activo ? 'bg-green-600' : 'bg-gray-600'}`}>
                      {anuncio.activo ? 'Activo' : 'Inactivo'}
                    </div>
                  </div>
                  <a 
                    href={anuncio.enlace} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 mb-3"
                  >
                    <ExternalLink className="h-3 w-3" /> {anuncio.enlace.substring(0, 40)}...
                  </a>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={anuncio.activo} 
                        onCheckedChange={() => handleToggleActivo(anuncio)}
                      />
                      <span className="text-gray-400 text-sm">{anuncio.activo ? 'Activo' : 'Inactivo'}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(anuncio)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(anuncio.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-gray-900 text-white border-gray-700">
            <DialogHeader>
              <DialogTitle className="text-gold flex items-center gap-2">
                <Megaphone className="h-5 w-5" /> {editingId ? 'Editar' : 'Nuevo'} Anuncio
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-white">URL de la imagen *</Label>
                <Input
                  value={formData.imagen_url}
                  onChange={(e) => setFormData({ ...formData, imagen_url: e.target.value })}
                  placeholder="https://..."
                  className="bg-gray-800 border-gray-600"
                  required
                />
              </div>
              <div>
                <Label className="text-white">Enlace de destino *</Label>
                <Input
                  value={formData.enlace}
                  onChange={(e) => setFormData({ ...formData, enlace: e.target.value })}
                  placeholder="https://..."
                  className="bg-gray-800 border-gray-600"
                  required
                />
                <p className="text-gray-500 text-xs mt-1">A dónde irá el usuario al hacer clic</p>
              </div>
              {formData.imagen_url && (
                <div>
                  <Label className="text-gray-400 text-sm">Vista previa</Label>
                  <img 
                    src={formData.imagen_url} 
                    alt="Preview"
                    className="w-full h-24 object-cover rounded-lg mt-2 border border-gray-700"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <Switch 
                  checked={formData.activo} 
                  onCheckedChange={(checked) => setFormData({ ...formData, activo: checked })}
                />
                <Label className="text-white">Anuncio activo</Label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1 border-gray-600" onClick={() => setShowDialog(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 btn-primary-mk">
                  {editingId ? 'Guardar' : 'Crear'} Anuncio
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default GestionAnuncios;
